import React from "react";
import {
  Button,
  FormControl,
  FormErrorMessage,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useApolloClient } from "@apollo/client";
import { useTranslations } from "next-intl";
import { Controller, useForm } from "react-hook-form";

import { CreateStripeCheckoutUrlDocument, type ISubscriptionPlanByPkQuery } from "lib/api/sdk";
import { useUser } from "lib/contexts/useUser";
import useTranslationConstants from "lib/utils/constants";
import { useErrorToast } from "lib/utils/toasts/toasts";
import Prices from "pages/Register/Steps/StepFive/Prices";
import type { SportsTranslations } from "src/contexts/LocalesContext";
import { useLocales } from "src/contexts/LocalesContext";

type SubscribeNewPlanModalProps = {
  plan: ISubscriptionPlanByPkQuery["subscription_plan_by_pk"];
  isOpen: boolean;
  onClose: () => void;
};

const SubscribeNewPlanModal = ({
  isOpen,
  onClose,
  plan,
}: SubscribeNewPlanModalProps) => {
  const t = useTranslations("pages.coach");
  const apolloClient = useApolloClient();
  const { hasAccess, athlete } = useUser();
  const { getTranslations } = useLocales();
  const errorToast = useErrorToast();
  const { getSubscriptionPlanName } = useTranslationConstants();

  const FormController = useForm({
    defaultValues: {
      stripe_price_lookup_key: "",
    },
  });

  const spName = plan?.level ? getSubscriptionPlanName(plan?.level) : "";

  const sportTranslations = getTranslations<SportsTranslations>(plan?.sport?.sport_translations as SportsTranslations[]);

  const resetModal = () => {
    FormController.reset();
    onClose();
  };

  const submit = () => {
    try {
      if (!FormController.watch("stripe_price_lookup_key").length) return;

      processStripePayment(FormController.watch("stripe_price_lookup_key"));
      resetModal();
    } catch (error) {
      errorToast({ error });
    }
  };

  const processStripePayment = async (stripe_price_id: string) => {
    try {
      let stripeUrl;
      if (athlete?.stripe_customer_id) {
        stripeUrl = await apolloClient.query({
          query: CreateStripeCheckoutUrlDocument,
          variables: { stripe_price_id, customer: athlete.stripe_customer_id },
          fetchPolicy: "network-only"
        });
      } else {
        stripeUrl = await apolloClient.query({
          query: CreateStripeCheckoutUrlDocument,
          variables: { stripe_price_id, email: athlete?.user?.email },
          fetchPolicy: "network-only"
        });
      }

      window.location.href = stripeUrl.data.create_stripe_checkout_url;
    } catch (error) {
      errorToast({ error });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={resetModal} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`${plan?.coach?.name} - ${sportTranslations?.title} (${spName})`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" w="full" spacing="5" as="form">
            <Text>{t("chooseSubscriptionPlan")}</Text>
            <FormControl isInvalid={!!FormController.formState.errors?.stripe_price_lookup_key}>
              <VStack w="full" spacing={5}>
                <Controller
                  name="stripe_price_lookup_key"
                  control={FormController.control}
                  rules={{
                    required: t("requiredSubscriptionPlanInputMessage"),
                  }}
                  render={({ field }) => {
                    return (
                      <Prices
                        {...field}
                        data={{ subscription_plan_by_pk: plan }}
                      />
                    );
                  }}
                />
                <FormErrorMessage>
                  {FormController.formState.errors?.stripe_price_lookup_key?.message}
                </FormErrorMessage>
                <Text fontSize="sm" textAlign="center">
                  {t("promoCode")}
                </Text>
                {hasAccess &&
                  <Text fontSize="sm" textAlign="center" color="red.400">
                    {t("thisWillCancelCurrentSub")}
                  </Text>
                }
              </VStack>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={resetModal}>
            {t("cancel")}
          </Button>
          <Button
            colorScheme="brandOrange"
            onClick={FormController.handleSubmit(submit)}
          >
            {t("payment")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SubscribeNewPlanModal;
